import { IconName } from '@ardoq/icons';
import { forwardRef, useEffect, useState } from 'react';
import { TextInput, TextInputProps } from './TextInput';
import { getIsCssColorValue } from '@ardoq/color-helpers';
import { zIndex } from '@ardoq/design-tokens';

type HEXColor = string;

export type HexInputProps = Omit<
  TextInputProps,
  'onValueChange' | 'value' | 'defaultValue' | 'rightIconName'
> & {
  allowBlank?: boolean;
  fallbackColor?: string;
  onValueChange?: (value: HEXColor) => void;
  value?: HEXColor | null;
  defaultValue?: HEXColor;
  skipIcon?: boolean;
  ignoreTransparencies?: boolean;
  isColorPickerOpen?: boolean;
  onClear?: () => void;
  onBlur?: () => void;
};

const getIsWhite = (color: string) => {
  if (!getIsCssColorValue(color)) return false;
  return color.toUpperCase().match(/^#(?:[F]{3,6})$/);
};

export const FULL_TRANSPARENT = '#ffffff00';

const getInputIcon = (value: string, ignoreTransparencies?: boolean) => {
  if (value === '') return FULL_TRANSPARENT as IconName;
  if (getIsWhite(value)) return IconName.CHECKBOX_UNCHECKED;
  if (getIsCssColorValue(value, { ignoreTransparencies })) {
    return value as IconName;
  }
  return IconName.HELP;
};

export const HexInput = forwardRef<HTMLInputElement, HexInputProps>(
  (
    {
      hasError,
      defaultValue,
      value,
      fallbackColor = '',
      onValueChange,
      skipIcon,
      ignoreTransparencies,
      isColorPickerOpen,
      onClear,
      ...props
    },
    ref
  ) => {
    const [lastValidValue, setlastValidValue] = useState<string>(
      (getIsCssColorValue(value || '', { ignoreTransparencies }) && value) ||
        (getIsCssColorValue(defaultValue || '', { ignoreTransparencies }) &&
          defaultValue) ||
        fallbackColor
    );
    const [textInputValue, setTextInputValue] =
      useState<string>(lastValidValue);
    useEffect(() => {
      const valueToSet = value || fallbackColor;
      if (value && getIsCssColorValue(valueToSet, { ignoreTransparencies })) {
        setlastValidValue(valueToSet);
        setTextInputValue(valueToSet);
      } else {
        setlastValidValue(fallbackColor);
        setTextInputValue(fallbackColor);
      }
    }, [value, fallbackColor, ignoreTransparencies]);

    return (
      <TextInput
        {...props}
        onClear={onClear}
        ref={ref}
        __DS_ONLY_fieldStyle={
          (isColorPickerOpen && {
            position: 'relative',
            // When color picker dropdown is opened
            // we need to render input on the top of
            // backdrop so it stays interactive
            zIndex: zIndex.MODAL_BACKDROP + 1,
          }) ||
          undefined
        }
        hasError={hasError}
        hideErrorIcon
        leftIconName={
          !skipIcon
            ? getInputIcon(textInputValue, ignoreTransparencies)
            : undefined
        }
        value={textInputValue}
        onValueChange={newValue => {
          const withHash = `#${newValue}`.replace(/^#+/, '#');
          if (newValue === '') {
            setlastValidValue(fallbackColor);
            setTextInputValue('');
          } else if (getIsCssColorValue(withHash, { ignoreTransparencies })) {
            setlastValidValue(withHash.toUpperCase());
            setTextInputValue(withHash);
            onValueChange?.(withHash);
          } else {
            setTextInputValue(newValue);
          }
        }}
        onBlur={() => {
          setTextInputValue(lastValidValue);
          onValueChange?.(lastValidValue);
          props.onBlur?.();
        }}
      />
    );
  }
);
